import { useState } from 'react';
import { PageHero } from '@/components/PageHero';
import { FaqAccordion } from '@/components/FaqAccordion';
import { Reveal } from '@/components/Reveal';
import { SuccessPanel } from '@/components/ContactForm';
import { useSeo } from '@/components/Seo';
import { STATIC_IT } from '@/data/staticIt';
import { STATIC_EN } from '@/data/staticEn';
import { CLAIM_AREAS_IT, CLAIM_AREAS_EN } from '@/data/productPages';
import { useLang } from '@/i18n/LangContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { PublicFileDropzone } from '@/components/PublicFileDropzone';
import { PublicPhoneInput, publicPhoneValue } from '@/components/PublicPhoneInput';
import { completePublicClaimSubmission, submitPublicForm, uploadPublicClaimFile } from '@/lib/publicForms';
import {
  Car, Home as HomeIcon, ShieldAlert, Bandage, Stethoscope, Plane, Scale, Building2, Bug,
  HeartHandshake, HelpCircle, Camera, PhoneCall, Loader2, Send, ArrowLeft, ArrowRight,
  Check,
} from 'lucide-react';

const CLAIM_ICONS = {
  veicolo: Car,
  casa: HomeIcon,
  rc: ShieldAlert,
  infortunio: Bandage,
  malattia: Stethoscope,
  viaggio: Plane,
  giuridica: Scale,
  azienda: Building2,
  cyber: Bug,
  previdenza: HeartHandshake,
  altro: HelpCircle,
};

const COPY = {
  it: {
    steps: ['Tipo di sinistro', 'Contatto', 'Dettagli', 'Documenti'],
    title: 'Segnala il sinistro in quattro passaggi',
    intro: 'Inizia dall’area interessata. Potrai controllare i dati prima dell’invio.',
    typeTitle: 'Quale area riguarda il sinistro?',
    identityTitle: 'Chi dobbiamo contattare?',
    detailsTitle: 'Quando e dove è successo?',
    documentsTitle: 'Descrivi il sinistro e allega i documenti',
    name: 'Nome completo o azienda',
    namePlaceholder: 'Mario Rossi oppure Azienda SA',
    contactHint: 'Email e telefono sono necessari per il riepilogo e per ricontattarti. I dati aiutano anche a identificare il cliente nel sistema.',
    address: 'Indirizzo (usato solo per distinguere eventuali omonimi)',
    street: 'Indirizzo', houseNumber: 'Numero civico', postalCode: 'CAP', city: 'Città',
    email: 'Email',
    phone: 'Telefono',
    date: 'Data del sinistro',
    place: 'Luogo',
    placePlaceholder: 'Località, indirizzo o paese',
    policy: 'Numero di polizza',
    policyPlaceholder: 'Se disponibile',
    plate: 'Numero di targa',
    description: 'Descrizione del sinistro',
    descriptionPlaceholder: 'Racconta cosa è successo, quali danni hai rilevato e chi era coinvolto.',
    files: 'Allega file',
    filesHint: 'Trascina qui PDF o immagini, oppure selezionali. Dimensione massima: 25 MB per file.',
    privacy: 'Autorizzo il trattamento dei dati per la gestione della segnalazione e dichiaro che le informazioni fornite sono corrette.',
    marketing: 'Desidero ricevere aggiornamenti utili via email (Facoltativo).',
    back: 'Indietro',
    next: 'Continua',
    required: 'Completa i campi richiesti per continuare.',
    contactRequired: 'Inserisci un indirizzo email valido e un numero di telefono.',
    fileTooLarge: 'Uno o più file superano il limite di 25 MB. Rimuovili e riprova.',
    fileTypeInvalid: 'Sono accettati PDF e immagini JPG, PNG o HEIC.',
    dropFiles: 'Trascina qui i documenti',
    typeSelected: 'Area selezionata',
  },
  en: {
    steps: ['Claim type', 'Contact', 'Details', 'Documents'],
    title: 'Report your claim in four steps',
    intro: 'Start with the relevant area. You can review the information before sending it.',
    typeTitle: 'Which area does the claim concern?',
    identityTitle: 'Who should we contact?',
    detailsTitle: 'When and where did it happen?',
    documentsTitle: 'Describe the claim and attach documents',
    name: 'Full name or company',
    namePlaceholder: 'Jane Smith or Company Ltd',
    contactHint: 'Email and phone are required for the recap and follow-up. These details also help identify the customer.',
    address: 'Address (used only to distinguish possible duplicate records)',
    street: 'Address', houseNumber: 'House number', postalCode: 'Postcode', city: 'Town',
    email: 'Email',
    phone: 'Phone',
    date: 'Claim date',
    place: 'Location',
    placePlaceholder: 'Town, address or country',
    policy: 'Policy number',
    policyPlaceholder: 'If available',
    plate: 'Registration plate',
    description: 'Claim description',
    descriptionPlaceholder: 'Explain what happened, what was damaged and who was involved.',
    files: 'Attach files',
    filesHint: 'Drag PDFs or images here, or select them. Maximum size: 25 MB per file.',
    privacy: 'I authorise data processing for this claim and confirm that the information provided is correct.',
    marketing: 'I would like to receive useful updates by email (optional).',
    back: 'Back',
    next: 'Continue',
    required: 'Complete the required fields to continue.',
    contactRequired: 'Enter a valid email address and a phone number.',
    fileTooLarge: 'One or more files exceed the 25 MB limit. Remove them and try again.',
    fileTypeInvalid: 'PDF and JPG, PNG or HEIC images are accepted.',
    dropFiles: 'Drop documents here',
    typeSelected: 'Selected area',
  },
};

const StepProgress = ({ step, labels }) => (
  <ol className="grid grid-cols-4 gap-2 mb-10" aria-label="Progressione modulo">
    {labels.map((label, index) => {
      const number = index + 1;
      const active = number === step;
      const complete = number < step;
      return (
        <li key={label} className="min-w-0">
          <div className={`h-1.5 rounded-full mb-3 ${number <= step ? 'bg-primary' : 'bg-border'}`} />
          <div className="flex items-center gap-2">
            <span className={`h-7 w-7 shrink-0 rounded-full border flex items-center justify-center text-xs font-bold ${
              active ? 'bg-primary border-primary text-white' : complete ? 'bg-brand-pale border-primary text-primary' : 'border-border text-muted-foreground'
            }`}>
              {complete ? <Check className="h-4 w-4" /> : number}
            </span>
            <span className={`hidden sm:block truncate text-xs font-semibold ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
          </div>
        </li>
      );
    })}
  </ol>
);

const ClaimsWizard = () => {
  const { lang, t } = useLang();
  const copy = COPY[lang];
  const areas = lang === 'en' ? CLAIM_AREAS_EN : CLAIM_AREAS_IT;
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const [values, setValues] = useState({
    name: '', email: '', phonePrefix: '+41', phoneNumber: '', street: '', houseNumber: '', postalCode: '', city: '', date: '', place: '', policy: '', plate: '', description: '',
  });

  const setField = (field) => (event) => {
    const { value } = event.target;
    setValues((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const validate = () => {
    if (step === 1 && !selectedType) return copy.required;
    if (step === 2) {
      if (!values.name.trim()) return copy.required;
      const phoneDigits = publicPhoneValue(values.phonePrefix, values.phoneNumber).replace(/\D/g, '');
      if (!/^\S+@\S+\.\S+$/.test(values.email.trim()) || phoneDigits.length < 6) return copy.contactRequired;
    }
    if (step === 3 && (!values.date || !values.place.trim() || (selectedType === 'veicolo' && !values.plate.trim()))) return copy.required;
    if (step === 4 && (!values.description.trim() || !privacy)) return copy.required;
    return '';
  };

  const next = () => {
    const message = validate();
    if (message) {
      setError(message);
      return;
    }
    setError('');
    setStep((current) => Math.min(4, current + 1));
  };

  const submit = async (event) => {
    event.preventDefault();
    const message = validate();
    if (message) {
      setError(message);
      return;
    }
    setState('sending');
    try {
      const submission = await submitPublicForm('/public/v1/claim-submissions', {
        customer_name: values.name.trim(), email: values.email.trim() || null, phone: publicPhoneValue(values.phonePrefix, values.phoneNumber) || null,
        policy_number: values.policy.trim() || null, address: {
          street: values.street.trim() || null, house_number: values.houseNumber.trim() || null,
          postal_code: values.postalCode.trim() || null, city: values.city.trim() || null,
        },
        event_date: values.date, event_location: values.place.trim(), claim_type: selectedType,
        plate: values.plate.trim() || null, description: values.description.trim(),
        attachments: files.map((file) => ({ file_name: file.name, mime_type: file.type, size_bytes: file.size })),
        locale: lang === 'en' ? 'en-GB' : 'it-CH', privacy_notice_version: '2026-08', privacy_consent: true, marketing_consent: marketing, website: '',
      });
      if (!submission.public_token) throw new Error(lang === 'en' ? 'Secure submission is temporarily unavailable.' : 'L’invio sicuro è temporaneamente non disponibile.');
      if (files.length) {
        for (const file of files) await uploadPublicClaimFile(submission.public_token, file);
      }
      await completePublicClaimSubmission(submission.public_token);
      setState('sent');
    } catch (requestError) {
      setState('error');
      setError(requestError?.message || (lang === 'en' ? 'The claim could not be sent. Please try again.' : 'Non è stato possibile inviare il sinistro. Riprova.'));
    }
  };

  const newRequest = () => {
    setStep(1);
    setSelectedType('');
    setPrivacy(false);
    setMarketing(false);
    setState('idle');
    setError('');
    setFiles([]);
    setValues({
      name: '', email: '', phonePrefix: '+41', phoneNumber: '', street: '', houseNumber: '', postalCode: '', city: '', date: '', place: '', policy: '', plate: '', description: '',
    });
  };

  if (state === 'sent') {
    return <SuccessPanel testid="claim-form-success" title={t('claimSuccessTitle')} text={t('claimSuccessText')} onNewRequest={newRequest} newRequestLabel={lang === 'en' ? 'Report another claim' : 'Apri una nuova richiesta'} />;
  }

  const selectedLabel = areas.find((area) => area.key === selectedType)?.label;

  return (
    <form data-testid="claim-form" onSubmit={submit} noValidate>
      <StepProgress step={step} labels={copy.steps} />

      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">{copy.steps[step - 1]}</p>
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
          {[copy.typeTitle, copy.identityTitle, copy.detailsTitle, copy.documentsTitle][step - 1]}
        </h2>
        {step > 1 && selectedLabel && <p className="mt-2 text-sm text-muted-foreground">{copy.typeSelected}: <span className="font-semibold text-foreground">{selectedLabel}</span></p>}
      </div>

      {step === 1 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="claim-type-grid">
          {areas.map((area) => {
            const Icon = CLAIM_ICONS[area.key];
            const selected = selectedType === area.key;
            return (
              <button
                key={area.key}
                type="button"
                data-testid={`claim-type-${area.key}`}
                onClick={() => { setSelectedType(area.key); setError(''); }}
                aria-pressed={selected}
                className={`min-h-28 p-4 rounded-xl border flex flex-col items-center justify-center gap-3 text-center transition-colors ${
                  selected ? 'bg-brand-deep border-brand-deep text-white' : 'bg-white border-border hover:border-primary hover:bg-brand-pale/50'
                }`}
              >
                <Icon className={`h-6 w-6 ${selected ? 'text-brand-light' : 'text-primary'}`} />
                <span className="text-sm font-semibold leading-tight">{area.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div>
            <Label htmlFor="cl-name" className="text-sm font-bold mb-1.5 block">{copy.name}</Label>
            <Input id="cl-name" data-testid="claim-form-nome" value={values.name} onChange={setField('name')} placeholder={copy.namePlaceholder} className="rounded-lg h-11" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="cl-email" className="text-sm font-bold mb-1.5 block">{copy.email}</Label>
              <Input id="cl-email" data-testid="claim-form-email" type="email" required value={values.email} onChange={setField('email')} placeholder="nome@esempio.ch" className="rounded-lg h-11" />
            </div>
            <PublicPhoneInput id="cl-phone" label={copy.phone} required lang={lang} prefix={values.phonePrefix} number={values.phoneNumber} onPrefixChange={(value) => setValues((current) => ({ ...current, phonePrefix: value }))} onNumberChange={(value) => setValues((current) => ({ ...current, phoneNumber: value }))} autoComplete="tel" testId="claim-form-telefono" />
          </div>
          <p className="text-xs text-muted-foreground">{copy.contactHint}</p>
          <fieldset className="rounded-xl border border-border p-4">
            <legend className="px-1 text-xs font-bold text-muted-foreground">{copy.address} ({lang === 'en' ? 'optional' : 'Facoltativo'})</legend>
            <div className="grid sm:grid-cols-5 gap-3">
              <Input aria-label={copy.street} value={values.street} onChange={setField('street')} placeholder={copy.street} className="sm:col-span-2 rounded-lg h-11" />
              <Input aria-label={copy.houseNumber} value={values.houseNumber} onChange={setField('houseNumber')} placeholder={copy.houseNumber} className="rounded-lg h-11" />
              <Input aria-label={copy.postalCode} value={values.postalCode} onChange={setField('postalCode')} placeholder={copy.postalCode} className="rounded-lg h-11" />
              <Input aria-label={copy.city} value={values.city} onChange={setField('city')} placeholder={copy.city} className="rounded-lg h-11" />
            </div>
          </fieldset>
        </div>
      )}

      {step === 3 && (
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="cl-date" className="text-sm font-bold mb-1.5 block">{copy.date}</Label>
            <Input id="cl-date" data-testid="claim-form-data" type="date" max={new Date().toISOString().slice(0, 10)} value={values.date} onChange={setField('date')} placeholder={copy.date} className="rounded-lg h-11" />
          </div>
          <div>
            <Label htmlFor="cl-place" className="text-sm font-bold mb-1.5 block">{copy.place}</Label>
            <Input id="cl-place" data-testid="claim-form-luogo" value={values.place} onChange={setField('place')} placeholder={copy.placePlaceholder} className="rounded-lg h-11" />
          </div>
          <div>
            <Label htmlFor="cl-policy" className="text-sm font-bold mb-1.5 block">{copy.policy} <span className="font-normal text-muted-foreground">({lang === 'en' ? 'optional' : 'Facoltativo'})</span></Label>
            <Input id="cl-policy" data-testid="claim-form-polizza" value={values.policy} onChange={setField('policy')} placeholder={copy.policyPlaceholder} className="rounded-lg h-11" />
          </div>
          {selectedType === 'veicolo' && (
            <div>
              <Label htmlFor="cl-plate" className="text-sm font-bold mb-1.5 block">{copy.plate}</Label>
              <Input id="cl-plate" data-testid="claim-form-targa" value={values.plate} onChange={setField('plate')} placeholder={lang === 'en' ? 'e.g. TI 123456' : 'Es. TI 123456'} className="rounded-lg h-11 uppercase" />
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-6">
          <div>
            <Label htmlFor="cl-description" className="text-sm font-bold mb-1.5 block">{copy.description}</Label>
            <Textarea id="cl-description" data-testid="claim-form-descrizione" rows={6} value={values.description} onChange={setField('description')} placeholder={copy.descriptionPlaceholder} className="rounded-lg resize-none" />
          </div>
          <PublicFileDropzone
            id="cl-file"
            files={files}
            onFilesChange={(next) => { setFiles(next); setError(''); }}
            maxFiles={10}
            lang={lang}
            testId="claim-form-upload"
            dropzoneTestId="claim-form-dropzone"
            label={`${copy.files} (${lang === 'en' ? 'optional' : 'Facoltativo'})`}
          />
          <div className="flex items-start gap-3">
            <Checkbox id="cl-privacy" data-testid="claim-form-privacy-checkbox" checked={privacy} onCheckedChange={(checked) => { setPrivacy(Boolean(checked)); setError(''); }} className="mt-0.5 rounded-sm" />
            <Label htmlFor="cl-privacy" className="text-xs leading-relaxed text-muted-foreground cursor-pointer">{copy.privacy}</Label>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox id="cl-marketing" data-testid="claim-form-marketing-checkbox" checked={marketing} onCheckedChange={(checked) => setMarketing(Boolean(checked))} className="mt-0.5 rounded-sm" />
            <Label htmlFor="cl-marketing" className="text-xs leading-relaxed text-muted-foreground cursor-pointer">{copy.marketing}</Label>
          </div>
        </div>
      )}

      {error && <p role="alert" className="mt-5 text-sm font-medium text-destructive">{error}</p>}

      <div className="mt-8 pt-6 border-t border-border flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        {step > 1 ? (
          <button type="button" onClick={() => { setStep((current) => current - 1); setError(''); }} className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold rounded-lg border border-border hover:border-primary transition-colors">
            <ArrowLeft className="h-4 w-4" />{copy.back}
          </button>
        ) : <span />}
        {step < 4 ? (
          <button type="button" data-testid="claim-form-next" onClick={next} className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold rounded-lg hover:bg-brand-dark transition-colors">
            {copy.next}<ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="submit" data-testid="claim-form-submit" disabled={state === 'sending'} className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-3 text-sm font-semibold rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {state === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{t('claimSubmit')}
          </button>
        )}
      </div>
    </form>
  );
};

export default function CentroSinistri() {
  const { lang, t } = useLang();
  const c = lang === 'en' ? STATIC_EN.centroSinistri : STATIC_IT.centroSinistri;
  const copy = COPY[lang];
  useSeo({ ...c.seo, faq: c.faq });

  return (
    <div data-testid="centro-sinistri-page">
      <PageHero eyebrow={c.eyebrow} title={c.title} summary={c.summary} image="/assets/area-salute.webp" imageAlt={c.title} breadcrumbs={[{ label: t('breadcrumbHome'), href: '/' }, { label: c.eyebrow }]} />

      <section className="bg-white border-y border-border">
        <div className="mx-auto max-w-site px-5 md:px-8 py-14 md:py-20">
          <Reveal className="max-w-2xl mb-10">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-4">{c.step2Eyebrow}</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">{copy.title}</h2>
            <p className="text-foreground/70">{copy.intro}</p>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <Reveal className="lg:col-span-8 rounded-2xl border border-border bg-background p-5 sm:p-7 md:p-9">
              <ClaimsWizard />
            </Reveal>
            <Reveal className="lg:col-span-4 lg:sticky lg:top-28">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-4">{c.immediateEyebrow}</p>
              <h2 className="font-heading text-2xl font-bold tracking-tight mb-6">{c.immediateTitle}</h2>
              <ol className="space-y-4">
                {c.immediateSteps.map((item, index) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="font-heading text-sm font-bold text-brand-light shrink-0 w-7">{String(index + 1).padStart(2, '0')}</span>
                    <span className="text-sm leading-relaxed text-foreground/75">{item}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-7 border-l-4 border-primary bg-brand-pale p-5 rounded-r-xl flex items-start gap-3">
                <Camera className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm leading-relaxed text-foreground/80">{c.photoNote}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-site px-5 md:px-8 py-14 md:py-20">
        <Reveal>
          <div data-testid="urgent-note" className="border border-border bg-white p-6 md:p-8 rounded-xl flex items-start gap-4 mb-14">
            <span className="inline-flex bg-brand-pale p-2.5 rounded-lg shrink-0"><PhoneCall className="h-6 w-6 text-primary" /></span>
            <div>
              <h3 className="font-heading text-xl font-bold tracking-tight mb-2">{c.urgentTitle}</h3>
              <p className="text-sm md:text-base leading-relaxed text-foreground/75">{c.urgentText}</p>
            </div>
          </div>
        </Reveal>
        <FaqAccordion items={c.faq} eyebrow={c.faqEyebrow} title={c.faqTitle} />
      </section>
    </div>
  );
}
